import { useState, useCallback, useMemo, useEffect } from 'react';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import { VSpacingContainer, SearchInput } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { useUpdateDraftFilter } from 'src/components/Search/Common/Filters/hooks/draft/useUpdateDraftFilter';
import MetroIcon from 'src/components/Search/Common/Metro/MetroIcon';
import SelectorOption from 'src/components/Search/Resume/Filters/Geo/SelectorOption';
import { useGeoFiltersAnalytics } from 'src/components/Search/Resume/Filters/Geo/hooks/useAnalytics';
import { useSearchMetroState } from 'src/components/Search/Resume/Filters/Geo/hooks/useSearchMetroState';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { EMPTY_CLUSTER } from 'src/models/search/common/clusters';
import { ClusterKey } from 'src/types/search/common/clusters';

import LineContent from 'src/components/Search/Resume/Filters/Geo/Filters/Metro/LineContent';
import LineItem from 'src/components/Search/Resume/Filters/Geo/Filters/Metro/LineItem';
import { handleMetroOptionChange } from 'src/components/Search/Resume/Filters/Geo/Filters/Metro/utils/metroUtils';

const TrlKeys = {
    metroPlaceholder: 'search.resume.filters.geo.metro.placeholder',
};

/**
 * Фильтр с выбором метро.
 */
const Metro: TranslatedComponent = ({ trls }) => {
    const { selectedValues: selectedAreaValues } =
        useSelector((state) => state.searchDraftClusters[ClusterKey.Area]) || EMPTY_CLUSTER;
    const selectedAreaId = selectedAreaValues[0];
    const { groups: metroGroups, selectedValues: selectedMetro } = useSelector(
        (state) => state.searchDraftClusters[ClusterKey.Metro] || EMPTY_CLUSTER
    );
    const { query, searchItems, allItems, onQueryChange } = useSearchMetroState(selectedAreaId);
    const isSingleLine = allItems?.length === 1;
    const [openedLineId, setOpenedLineId] = useState<string | undefined>(isSingleLine ? allItems[0].id : undefined);
    const isLineOpened = openedLineId !== undefined;
    const openedLineItem = useMemo(() => allItems?.find((line) => line.id === openedLineId), [allItems, openedLineId]);

    const applyDraftFilter = useUpdateDraftFilter(ClusterKey.Metro);

    useEffect(() => {
        setOpenedLineId(isSingleLine ? allItems[0].id : undefined);
    }, [allItems, isSingleLine]);

    const handleReturn = useCallback(() => {
        setOpenedLineId(undefined);
    }, [setOpenedLineId]);

    const handleQueryChange = useCallback(
        (value: string) => {
            onQueryChange(value);
            handleReturn();
        },
        [handleReturn, onQueryChange]
    );

    const { sendEventShowMetroFilter } = useGeoFiltersAnalytics();

    return (
        <ElementShownAnchor fn={sendEventShowMetroFilter}>
            <VSpacingContainer default={16}>
                <SearchInput
                    value={query}
                    onChange={handleQueryChange}
                    placeholder={trls[TrlKeys.metroPlaceholder]}
                    clearable
                />
                {isLineOpened && openedLineItem ? (
                    <LineContent line={openedLineItem} isSingleLine={isSingleLine} onReturn={handleReturn} />
                ) : (
                    <VSpacingContainer default={12}>
                        {searchItems.map(({ id, text, additional, items }) => {
                            const splitedId = id.split('.');
                            const isLine = !!items;
                            const isChecked = selectedMetro.some((item) => item === id || item === splitedId[0]);

                            return isLine ? (
                                <LineItem
                                    key={id}
                                    id={id}
                                    text={text}
                                    allItems={items}
                                    isChecked={isChecked}
                                    color={additional?.color || ''}
                                    onClick={setOpenedLineId}
                                />
                            ) : (
                                <SelectorOption
                                    key={id}
                                    isSelected={isChecked}
                                    onClick={() => {
                                        const lineId = splitedId[0];
                                        const allOptions = allItems.find((item) => item.id === lineId)?.items || [];
                                        handleMetroOptionChange(id, selectedMetro, allOptions, applyDraftFilter);
                                    }}
                                    text={text}
                                    textIcon={<MetroIcon isLine={false} color={additional?.color || ''} />}
                                    count={metroGroups[id]?.count || 0}
                                />
                            );
                        })}
                    </VSpacingContainer>
                )}
            </VSpacingContainer>
        </ElementShownAnchor>
    );
};

export default translation(Metro);
